/**
 * Guide tab: the handbook. What to expect day by day, the visit plan,
 * when to reach out, mood support, the partner playbook, and the
 * before-baby checklist.
 *
 * Sections are collapsible cards (native <details>), ordered by role and
 * phase: partners see partner support first, mom sees what-to-expect
 * first, and the prenatal phase pins the before-baby section on top.
 */
import type { ReactNode } from 'react';
import { useNav } from '../App';
import { Icons, ScreenHeader } from '../components/ui';
import { useLocale, useT } from '../i18n/i18n';
import {
  LACTATION_PHONE_DISPLAY,
  LACTATION_PHONE_TEL,
  OFFICE_PHONE_DISPLAY,
  OFFICE_PHONE_TEL,
  PRACTICE_NAME,
  SCHEDULE_URL,
  WEBSITE_URL,
} from '../lib/config';
import { expectationsFor } from '../lib/expectations';
import type { VisitKey } from '../lib/reminders';
import { useApp } from '../lib/store';
import { dayOfLife, formatDate } from '../lib/time';
import { mlToDisplay } from '../lib/units';

type SectionKey = 'prenatal' | 'expect' | 'visits' | 'reach' | 'mood' | 'partner';

const VISIT_KEYS: VisitKey[] = ['newborn', 'lactation', 'twoWeek', 'oneMonth'];

const SUMMARY_CLASS =
  'flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden';

/** Pill-shaped tappable link on a soft-colored background. */
function pillClass(textColor: string): string {
  return `inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-line bg-surface px-4 text-sm font-bold ${textColor}`;
}

function Section({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <details className="card group overflow-hidden" open={defaultOpen}>
      <summary className={SUMMARY_CLASS}>
        <h2 className="text-base leading-snug">{title}</h2>
        <span
          className="shrink-0 text-ink-faint transition-transform duration-200 group-open:rotate-90"
          aria-hidden="true"
        >
          {Icons.chevronRight('h-5 w-5')}
        </span>
      </summary>
      <div className="border-t border-line px-4 pb-4 pt-3">{children}</div>
    </details>
  );
}

const TIER_TONES = {
  accent: { card: 'bg-accent-soft', title: 'text-accent' },
  warn: { card: 'bg-warn-soft', title: 'text-warn' },
  bad: { card: 'bg-bad-soft', title: 'text-bad' },
} as const;

function TierCard({
  tone,
  title,
  items,
  note,
  actions,
}: {
  tone: keyof typeof TIER_TONES;
  title: string;
  items: readonly string[];
  note?: string;
  actions?: ReactNode;
}) {
  const c = TIER_TONES[tone];
  return (
    <div className={`rounded-2xl p-4 ${c.card}`}>
      <h3 className={`text-sm font-bold ${c.title}`}>{title}</h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {note ? <p className={`mt-3 text-sm font-bold ${c.title}`}>{note}</p> : null}
      {actions ? <div className="mt-3 flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export default function GuideScreen() {
  const t = useT();
  const locale = useLocale();
  const { device, settings, profile } = useApp();
  const { go } = useNav();

  const isPartner = device?.role === 'partner';
  const phase = profile?.phase ?? 'prenatal';
  // Clamp to Day 1 so a future birth date never renders "Day 0" or less.
  const rawDay = phase === 'baby' ? dayOfLife(profile?.birthAt) : null;
  const day = rawDay !== null ? Math.max(1, rawDay) : null;

  const order: SectionKey[] = isPartner
    ? ['partner', 'expect', 'visits', 'reach', 'mood']
    : ['expect', 'visits', 'reach', 'mood', 'partner'];
  if (phase === 'prenatal') order.unshift('prenatal');
  const first = order[0];

  const intakeRange = (minMl: number, maxMl: number): string => {
    const lo = mlToDisplay(minMl, settings.volumeUnit);
    const hi = mlToDisplay(maxMl, settings.volumeUnit);
    return lo === hi ? lo : t.guide.expect.range(lo, hi);
  };

  const bookedDates: Partial<Record<VisitKey, string | undefined>> = {
    newborn: profile?.newbornCheckupAt,
    lactation: profile?.lactationFollowupAt,
  };

  const todayExp = day !== null ? expectationsFor(day) : null;

  const sections: Record<SectionKey, ReactNode> = {
    // 6. Before baby arrives (prenatal phase only, pinned first)
    prenatal: (
      <Section key="prenatal" title={t.content.prenatal.title} defaultOpen={first === 'prenatal'}>
        <p className="text-sm text-ink-soft">{t.content.prenatal.intro}</p>
        <ul className="mt-3 space-y-2">
          {t.content.prenatal.checklist.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-ink">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
        <div className="mt-3 rounded-2xl bg-warn-soft p-3 text-sm text-ink">
          {t.content.prenatal.colostrumNote}
        </div>
      </Section>
    ),

    // 1. What to expect
    expect: (
      <Section key="expect" title={t.guide.expect.section} defaultOpen={first === 'expect'}>
        {todayExp && day !== null ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-bold">{t.content.expectations.title}</h3>
              <span className="shrink-0 rounded-full bg-primary-soft px-2.5 py-1 text-xs font-bold text-primary">
                {t.common.time.dayOfLife(day)}
              </span>
            </div>
            <div className="rounded-2xl bg-primary-soft p-3">
              <p className="text-sm font-bold text-primary">
                {t.content.stages[todayExp.stageKey].name}
              </p>
              <p className="mt-1 text-sm text-ink-soft">
                {t.content.stages[todayExp.stageKey].desc}
              </p>
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-ink">
              <li>{t.content.expectations.feeds(todayExp.feedsMin, todayExp.feedsMax)}</li>
              <li>{t.content.expectations.wets(todayExp.wetMin)}</li>
              <li>{t.content.expectations.dirties(todayExp.dirtyMin)}</li>
              <li>
                {t.content.expectations.intake(
                  intakeRange(todayExp.intakeMlMin, todayExp.intakeMlMax),
                )}
              </li>
            </ul>
          </div>
        ) : null}
        <div className={todayExp ? 'mt-4' : ''}>
          <h3 className="text-sm font-bold">{t.guide.expect.tableTitle}</h3>
          <p className="mt-0.5 text-xs text-ink-faint">{t.guide.expect.tableCaption}</p>
          <div className="mt-2 overflow-x-auto rounded-2xl border border-line">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-surface-2 text-left text-xs text-ink-faint">
                  <th className="px-3 py-2 font-bold">{t.guide.expect.colDay}</th>
                  <th className="px-3 py-2 font-bold">{t.guide.expect.colWet}</th>
                  <th className="px-3 py-2 font-bold">{t.guide.expect.colStool}</th>
                  <th className="px-3 py-2 font-bold">{t.guide.expect.colIntake}</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6].map((d) => {
                  const row = expectationsFor(d);
                  const active = day !== null && Math.min(day, 6) === d;
                  return (
                    <tr
                      key={d}
                      className={`border-t border-line ${active ? 'bg-primary-soft' : ''}`}
                    >
                      <td className={`px-3 py-2 font-bold ${active ? 'text-primary' : 'text-ink'}`}>
                        {d === 6 ? t.guide.expect.dayPlus(6) : d}
                      </td>
                      <td className="px-3 py-2 text-ink">{t.guide.expect.atLeast(row.wetMin)}</td>
                      <td className="px-3 py-2 text-ink">{t.guide.expect.atLeast(row.dirtyMin)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-ink">
                        {intakeRange(row.intakeMlMin, row.intakeMlMax)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-3 rounded-2xl bg-accent-soft p-3 text-sm text-ink">
          {t.content.expectations.wakeToFeed}
        </div>
        <p className="mt-3 text-sm text-ink-soft">{t.content.expectations.weightNote}</p>
      </Section>
    ),

    // 2. Your visit plan
    visits: (
      <Section key="visits" title={t.guide.visits.section} defaultOpen={first === 'visits'}>
        <div className="space-y-3">
          {VISIT_KEYS.map((key) => {
            const v = t.content.visits[key];
            const booked = bookedDates[key];
            return (
              <div key={key} className="rounded-2xl bg-surface-2 p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                  <h3 className="text-sm font-bold">{v.name}</h3>
                  <span className="text-xs font-bold text-primary">{v.window}</span>
                </div>
                <p className="mt-1 text-sm text-ink-soft">{v.desc}</p>
                {booked ? (
                  <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-good-soft px-2.5 py-1 text-xs font-bold text-good">
                    {Icons.calendar('h-3.5 w-3.5')}
                    {t.content.checkup.scheduledFor(formatDate(booked, locale))}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={SCHEDULE_URL}
            target="_blank"
            rel="noreferrer"
            className="btn-primary inline-flex min-h-[44px] items-center px-5 text-sm"
          >
            {t.content.checkup.scheduleOnline}
          </a>
          <a
            href={`tel:${OFFICE_PHONE_TEL}`}
            className="btn-secondary inline-flex min-h-[44px] items-center gap-1.5 px-5 text-sm"
          >
            {Icons.phone('h-4 w-4')}
            {t.content.checkup.callToSchedule}
          </a>
        </div>
      </Section>
    ),

    // 3. When to reach out
    reach: (
      <Section key="reach" title={t.content.whenToCall.title} defaultOpen={first === 'reach'}>
        <div className="space-y-3">
          <TierCard
            tone="accent"
            title={t.content.whenToCall.lactationLine.title}
            items={t.content.whenToCall.lactationLine.items}
            actions={
              <>
                <a href={`tel:${LACTATION_PHONE_TEL}`} className={pillClass('text-accent')}>
                  {Icons.phone('h-4 w-4')}
                  {t.guide.reach.call(LACTATION_PHONE_DISPLAY)}
                </a>
                <a href={`sms:${LACTATION_PHONE_TEL}`} className={pillClass('text-accent')}>
                  {t.guide.reach.text(LACTATION_PHONE_DISPLAY)}
                </a>
              </>
            }
          />
          <TierCard
            tone="warn"
            title={t.content.whenToCall.office.title}
            items={t.content.whenToCall.office.items}
            actions={
              <a href={`tel:${OFFICE_PHONE_TEL}`} className={pillClass('text-warn')}>
                {Icons.phone('h-4 w-4')}
                {t.guide.reach.call(OFFICE_PHONE_DISPLAY)}
              </a>
            }
          />
          <TierCard
            tone="bad"
            title={t.content.whenToCall.urgent.title}
            items={t.content.whenToCall.urgent.items}
            note={t.common.emergencyNote}
          />
        </div>
      </Section>
    ),

    // 4. Your mood. The crisis block stays visible even when collapsed.
    mood: (
      <div key="mood" className="card overflow-hidden">
        <details className="group" open={first === 'mood'}>
          <summary className={SUMMARY_CLASS}>
            <h2 className="text-base leading-snug">{t.content.mood.title}</h2>
            <span
              className="shrink-0 text-ink-faint transition-transform duration-200 group-open:rotate-90"
              aria-hidden="true"
            >
              {Icons.chevronRight('h-5 w-5')}
            </span>
          </summary>
          <div className="space-y-3 border-t border-line px-4 pb-4 pt-3">
            <p className="text-sm text-ink">{t.content.mood.normal}</p>
            <p className="text-sm text-ink">{t.content.mood.concerning}</p>
          </div>
        </details>
        <div className="border-t border-line bg-bad-soft px-4 py-4">
          <h3 className="text-sm font-bold text-bad">{t.guide.mood.crisisTitle}</h3>
          <p className="mt-1 text-sm text-ink">{t.content.mood.crisis}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href="tel:988" className={pillClass('text-bad')}>
              {Icons.phone('h-4 w-4')}
              {t.guide.mood.call988}
            </a>
            <a href="sms:988" className={pillClass('text-bad')}>
              {t.guide.mood.text988}
            </a>
            <a href="tel:18009444773" className={pillClass('text-bad')}>
              {t.guide.mood.callPsi}
            </a>
          </div>
        </div>
      </div>
    ),

    // 5. Partner support
    partner: (
      <Section key="partner" title={t.content.partnerTips.title} defaultOpen={first === 'partner'}>
        {isPartner ? <p className="text-sm text-ink-soft">{t.content.partnerTips.intro}</p> : null}
        <div className={`space-y-2.5 ${isPartner ? 'mt-3' : ''}`}>
          {t.content.partnerTips.tips.map((tip) => (
            <div key={tip.title} className="rounded-2xl bg-surface-2 p-3">
              <h3 className="text-sm font-bold">{tip.title}</h3>
              <p className="mt-0.5 text-sm text-ink-soft">{tip.body}</p>
            </div>
          ))}
        </div>
        {isPartner ? (
          <div className="mt-3 rounded-2xl bg-primary-soft p-3 text-sm text-ink">
            {t.content.mood.partnerNote}
          </div>
        ) : null}
      </Section>
    ),
  };

  return (
    <>
      <ScreenHeader
        title={t.guide.title}
        subtitle={t.guide.subtitle}
        onSettings={() => go('settings')}
      />
      <main className="space-y-3 px-4 py-4">
        {order.map((key) => sections[key])}

        {/* 7. Footer: the practice, one tap away */}
        <div className="card p-4">
          <h2 className="text-base">{PRACTICE_NAME}</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={`tel:${OFFICE_PHONE_TEL}`} className={pillClass('text-primary')}>
              {Icons.phone('h-4 w-4')}
              {OFFICE_PHONE_DISPLAY}
            </a>
            <a href={WEBSITE_URL} target="_blank" rel="noreferrer" className={pillClass('text-accent')}>
              {WEBSITE_URL.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-ink-faint">{t.common.disclaimer}</p>
        </div>
      </main>
    </>
  );
}
